import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { superAdminApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Shield, LogOut, Building2, Users, TrendingUp, Clock,
  CheckCircle, XCircle, Search, MoreVertical, ChevronDown,
  Loader2, RefreshCw, Star,
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';

export default function SuperAdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const loadData = useCallback(async () => {
    try {
      const [statsRes, tenantsRes] = await Promise.all([
        superAdminApi.getStats(),
        superAdminApi.getTenants(true),
      ]);
      setStats(statsRes.data);
      setTenants(tenantsRes.data);
    } catch {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user.role || user.role !== 'super_admin') {
      navigate('/super-admin/login');
      return;
    }
    loadData();
  }, [navigate, user.role, loadData]);

  const handleAction = async (action, tenantId, tenantName) => {
    setActionLoading(tenantId);
    try {
      if (action === 'validate') {
        await superAdminApi.validateTenant(tenantId);
        toast.success(`${tenantName} validee`);
      } else if (action === 'suspend') {
        await superAdminApi.suspendTenant(tenantId);
        toast.success(`${tenantName} suspendue`);
      } else if (action === 'activate') {
        await superAdminApi.activateTenant(tenantId);
        toast.success(`${tenantName} activee`);
      } else if (action === 'delete') {
        if (window.confirm(`Supprimer ${tenantName} ?`)) {
          await superAdminApi.deleteTenant(tenantId);
          toast.success(`${tenantName} supprimee`);
        }
      } else if (action.startsWith('plan_')) {
        const planId = action.replace('plan_', '');
        await superAdminApi.changePlan(tenantId, planId);
        toast.success(`Plan change en ${planId}`);
      }
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setActionLoading(null);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/super-admin/login');
  };

  const filteredTenants = tenants.filter(t => {
    if (filter === 'active' && !t.is_active) return false;
    if (filter === 'pending' && t.is_active) return false;
    if (search) {
      const q = search.toLowerCase();
      return t.name.toLowerCase().includes(q) || t.slug.toLowerCase().includes(q) || t.email.toLowerCase().includes(q);
    }
    return true;
  });

  const planBadge = (plan) => {
    const colors = { free: 'bg-stone-100 text-stone-700', premium: 'bg-amber-50 text-amber-700', enterprise: 'bg-violet-50 text-violet-700' };
    return <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${colors[plan] || colors.free}`}>{plan}</span>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-950 flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-stone-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-950">
      {/* Header */}
      <header className="bg-stone-900 border-b border-stone-800">
        <div className="max-w-7xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-emerald-700 flex items-center justify-center">
              <Shield className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-white text-sm">Daaraj Super Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-stone-400 text-sm hidden sm:block">{user.email}</span>
            <Button variant="ghost" size="sm" onClick={logout} className="text-stone-400 hover:text-white" data-testid="sa-logout-btn">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {[
              { label: 'Total Ecoles', value: stats.tenants.total, icon: Building2, color: 'text-emerald-400' },
              { label: 'Actives', value: stats.tenants.active, icon: CheckCircle, color: 'text-green-400' },
              { label: 'En attente', value: stats.tenants.pending, icon: Clock, color: 'text-amber-400' },
              { label: 'Premium', value: stats.plans.premium + stats.plans.enterprise, icon: Star, color: 'text-violet-400' },
            ].map((s, i) => (
              <Card key={i} className="bg-stone-900 border-stone-800" data-testid={`stat-card-${i}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-stone-500 text-xs">{s.label}</p>
                      <p className="text-2xl font-bold text-white mt-1">{s.value}</p>
                    </div>
                    <s.icon className={`w-5 h-5 ${s.color}`} />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Tenants */}
        <Card className="bg-stone-900 border-stone-800">
          <CardContent className="p-0">
            <div className="p-4 border-b border-stone-800 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <h2 className="text-base font-semibold text-white">Ecoles</h2>
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <div className="relative flex-1 sm:w-56">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" />
                  <Input
                    value={search} onChange={(e) => setSearch(e.target.value)}
                    placeholder="Rechercher..."
                    className="pl-9 bg-stone-800 border-stone-700 text-white text-sm h-9"
                    data-testid="search-tenants-input"
                  />
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="border-stone-700 text-stone-300 bg-stone-800 h-9">
                      {filter === 'all' ? 'Tous' : filter === 'active' ? 'Actifs' : 'En attente'}
                      <ChevronDown className="w-3 h-3 ml-1" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-stone-800 border-stone-700">
                    <DropdownMenuItem onClick={() => setFilter('all')} className="text-stone-300">Tous</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setFilter('active')} className="text-stone-300">Actifs</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setFilter('pending')} className="text-stone-300">En attente</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button variant="ghost" size="sm" onClick={loadData} className="text-stone-400 h-9" data-testid="refresh-btn">
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {filteredTenants.length === 0 ? (
              <div className="p-12 text-center text-stone-500 text-sm">Aucune ecole trouvee</div>
            ) : (
              <div className="divide-y divide-stone-800">
                {filteredTenants.map((t) => (
                  <div key={t.id} className="p-4 flex items-center justify-between hover:bg-stone-800/50 transition-colors" data-testid={`tenant-row-${t.slug}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-lg bg-stone-800 flex items-center justify-center shrink-0" style={{ backgroundColor: t.settings?.primary_color ? `${t.settings.primary_color}20` : undefined }}>
                        <Building2 className="w-4 h-4 text-stone-400" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-white truncate">{t.name}</span>
                          {planBadge(t.plan)}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-stone-500">
                          <span>{t.slug}</span>
                          <span>&middot;</span>
                          <span>{t.email}</span>
                          {t.city && <><span>&middot;</span><span>{t.city}</span></>}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0 ml-4">
                      {!t.is_active ? (
                        <Button
                          size="sm" onClick={() => handleAction('validate', t.id, t.name)}
                          disabled={actionLoading === t.id}
                          className="bg-emerald-700 hover:bg-emerald-800 text-xs h-8"
                          data-testid={`validate-btn-${t.slug}`}
                        >
                          {actionLoading === t.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><CheckCircle className="w-3 h-3 mr-1" />Valider</>}
                        </Button>
                      ) : (
                        <Badge variant="outline" className="border-green-800 text-green-400 text-xs">Active</Badge>
                      )}

                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="text-stone-400 h-8 w-8 p-0" data-testid={`tenant-menu-${t.slug}`}>
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="bg-stone-800 border-stone-700">
                          {t.is_active ? (
                            <DropdownMenuItem onClick={() => handleAction('suspend', t.id, t.name)} className="text-amber-400">Suspendre</DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem onClick={() => handleAction('activate', t.id, t.name)} className="text-green-400">Activer</DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => handleAction('plan_free', t.id, t.name)} className="text-stone-300">Plan Gratuit</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleAction('plan_premium', t.id, t.name)} className="text-stone-300">Plan Premium</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleAction('plan_enterprise', t.id, t.name)} className="text-stone-300">Plan Entreprise</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleAction('delete', t.id, t.name)} className="text-red-400">Supprimer</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
